import React from 'react';
import { IconCircleInfo } from '../../ui-kit/icons';
import { connect } from 'react-redux';
// Custom Imports
import { operations as activityTypeOperations } from '../../../state/modules/activity-types';
import { operations as activityReportOperations } from '../../../state/modules/activity-reports';
import { operations as incidentOperations } from '../../../state/modules/incidents';
import { operations as activityReportAttendanceOperations } from '../../../state/modules/activity-report-attendances';
import { operations as groupOperations } from '../../../state/modules/groups';
import { selectors as activityReportAttendancesSelectors } from '../../../state/modules/activity-report-attendances';
import { operations as userOperations } from '../../../state/modules/users';
import { operations as rankOperations } from '../../../state/modules/ranks';
import {
  operations as availabilityCodeOperations,
  selectors as availabilityCodeSelectors
} from '../../../state/modules/availability-codes';
import {
  overlappingAttendancesByUserId,
  activityOverlappingAttendances
} from '../../../state/modules/attendances/selectors';
import AttendancesTable from './AttendancesTable';
import AbstractActivityReportForm from '../AbstractActivityReportForm';

import {
  TextField,
  TypeSelector,
  AvailabilityCodeSelector,
  DescriptionField,
  PeriodSelectorField,
  NoActivityTypeWarning,
  LongActivityWarning
} from '../shared/Fields';
import { Badge } from '../../ui-kit';

const AvailabilityCodeExplanation = () => (
  <p className="my-1 small">
    <IconCircleInfo /> <Badge variant="secondary">{I18n.t('optional')} </Badge>{' '}
    {I18n.t('activity_reports.availability_code_info')}
  </p>
);

const Fields = ({
  report,
  updateFields,
  eligibleActivityTypes,
  updateDatetimeFields,
  selectAvailabilityCodeRights,
  availabilityCodes,
  availabilityCodesLoaded,
  updateAvailabilityCode
}) => (
  <>
    <TextField label={I18n.t('attributes.title')} name="title" value={report.title} onChange={updateFields} />
    <TypeSelector
      eligibleTypes={eligibleActivityTypes}
      selectedTypeIds={report.activity_type_ids}
      onChange={updateFields}
    />
    <NoActivityTypeWarning report={report} />
    {selectAvailabilityCodeRights && (
      <AvailabilityCodeSelector
        loading={!availabilityCodesLoaded}
        availabilityCodes={availabilityCodes}
        onChange={updateAvailabilityCode}
        selectedCodeIds={[report.availability_code_id]}
        multi={false}
        customInfoComponent={<AvailabilityCodeExplanation />}
      />
    )}
    <PeriodSelectorField startTime={report.start_time} endTime={report.end_time} onChange={updateDatetimeFields} />
    <LongActivityWarning report={report} />
    <DescriptionField value={report.description} onChange={updateFields} />
  </>
);

export const GeneralActivityReportForm = props => {
  const createNewAttendance = (membership, reportId, reportedStatus = 'unknown') => ({
    membership_id: membership.id,
    user_id: membership.user_id,
    group_id: membership.group_id,
    activity_report_id: reportId,
    reported_status: reportedStatus
  });

  const cancelPath = (groupId, reportId) => {
    return reportId
      ? `/groups/${groupId}/general_activity_reports/${reportId}`
      : `/groups/${groupId}/general_activity_reports`;
  };

  props = {
    ...props,
    cancelPath,
    createNewAttendance,
    // IMPORTANT: The following props are only needed to comply with tests
    // These can be removed once the tests are updated to handle a mock store
    // Because the OverlappingAttendancesWarning component can be directly
    // connected to the store
    fetchActivities: props.getActivityReport
  };

  return (
    <AbstractActivityReportForm
      {...props}
      FieldsComponent={Fields}
      AttendancesTableComponent={AttendancesTable}
      reportType="activity_report"
    />
  );
};

const mapStateToProps = state => ({
  activityReports: state.activityReports.byId,
  activityReportAttendances: activityReportAttendancesSelectors.augmentedAttendances(state),
  activityTypes: state.activityTypes.byId,
  groups: state.groups,
  ranksById: state.ranks.byId,
  availabilityCodes: availabilityCodeSelectors.selectForActivityReport(state),
  availabilityCodesLoaded: availabilityCodeSelectors.initialLoadComplete(state),
  overlappingAttendancesByUserId: overlappingAttendancesByUserId(state),
  activityOverlappingAttendances: activityOverlappingAttendances(state)
});

const mapDispatchToProps = {
  getActivityReport: activityReportOperations.fetch,
  createActivityReport: activityReportOperations.createNew,
  storeActivityReport: activityReportOperations.storeInBackend,
  updateReportFields: activityReportOperations.updateFields,
  createActivityReportAttendances: activityReportAttendanceOperations.createNewInBulk,
  storeActivityReportAttendance: activityReportAttendanceOperations.storeInBackend,
  updateAttendanceFields: activityReportAttendanceOperations.updateFields,
  fetchActivityTypes: activityTypeOperations.fetch,
  fetchUsers: userOperations.fetch,
  fetchGroups: groupOperations.fetch,
  fetchRanks: rankOperations.fetch,
  fetchAvailabilityCodes: availabilityCodeOperations.fetch,
  fetchIncidents: incidentOperations.fetch
};

export default connect(mapStateToProps, mapDispatchToProps)(GeneralActivityReportForm);
